<template>
  <card v-loading="loading">
    <el-dialog
      :visible.sync="modal.opened"
      width="500px"
      :fullscreen="$mq === 'xs'"
      :show-close="false">
      <div slot="title">
        <h4 class="title title-up">{{ modal.item.product_name }}</h4>
      </div>
      <p>{{$t(`Do you really want to ${modal.item.starts_in_future ? 'delete' : 'close'} the subscription?`)}}</p>
      <template slot="footer">
        <el-button plain class="cancel-button" @click.native="modal.opened = false">{{ $t('No') }}</el-button>
        <el-button type="danger" @click.native="closeSubscription(modal.item)">{{ $t('Yes') }}</el-button>
      </template>
    </el-dialog>
    <div slot="header" class='card-header'>
      <h4>{{ $t('Active subscriptions') }}</h4>
      <el-button icon="el-icon-refresh" size="mini" circle @click="$emit('refresh')"></el-button>
      <el-tooltip v-if="allowCreate" :content="$t('Add subscription')" :open-delay="300" placement="top">
        <n-button v-if="subscriptionsMenu" simple type="primary" round="" icon="" @click.native="goToProducts()" class="new-button">
          <i class="now-ui-icons ui-1_simple-add"></i>
        </n-button>
      </el-tooltip>
    </div>
    <div class="empty" v-if="data.length === 0">
      {{ $t('No subscriptions') }}
    </div>
    <ul v-else>
      <li v-for="subscription in data" :key="subscription.subscription_id" class="subscription">
        <div class="subscription__meta">
          <div class="subscription__name" :class="['subscription__name', { 'subscription__name--active': subscription.state > 0 && !subscription.current_blocking }]">
            <el-tooltip :content="processedTooltip(subscription)" :open-delay="300" placement="top">
              <i :class="{ 'el-icon-time': subscription.state <= 0, 'el-icon-lock':  subscription.current_blocking}" style="margin-right: 5px; font-size: 14px;" v-if="subscription.state <= 0 || subscription.current_blocking"></i>
            </el-tooltip>
            <el-tooltip :content="plannedChangesTooltip(subscription)" :open-delay="300" placement="top">
              <i class="el-icon-date" style="margin-right: 5px; font-size: 14px;" v-if="hasPlannedChanges(subscription)"></i>
            </el-tooltip>
            {{ subscription.descr ? subscription.descr : subscription.product_name }}
            <a v-if="subscription.product_link" :href="subscription.product_link" target="_blank" rel="noopener noreferrer">
              <el-tooltip :content="$t('Detailed information')" :open-delay="300" placement="top">
                <i class="el-icon-info" style="margin-left: 10px; color: #9A9A9A; font-size: 16px"></i>
              </el-tooltip>
            </a>
          </div>
          <div class="subscription__customName" style="color: #9A9A9A;" v-if="subscription.descr">
            {{ subscription.product_name }}
          </div>
          <div class="subscription__date">
            {{ period(subscription) }}
          </div>
        </div>
        <div class="subscription__price">
          <div>{{ `${formatPrice(subscription.monthly_cost)} ${subscription.cur_symbol}` }}</div>
          <div class="subscription__period">{{ getPeriodString(subscription.period_type, subscription.period_length) }}</div>
        </div>
        <div class="actions">
          <el-tooltip :content="$t('Edit')" :open-delay="300" placement="top">
            <el-button v-if="allowEdit && !hasPlannedChanges(subscription)" :disabled="editIsDisabled(subscription)" size="small" @click.prevent="editProduct(subscription)" icon="el-icon-edit" circle></el-button>
          </el-tooltip>
          <el-tooltip :content="$t('Open scheduled version')" :open-delay="300" placement="top">
            <el-button v-if="hasPlannedChanges(subscription)" size="small" @click.prevent="showPlannedChanges(subscription)" icon="el-icon-date" circle></el-button>
          </el-tooltip>
          <el-tooltip :content="$t(subscription.starts_in_future ? 'Delete' : 'Terminate')" :open-delay="300" placement="top">
            <el-button v-if="allowStop" :disabled="deleteIsDisabled(subscription)" size="small" @click.prevent="confirmDelete(subscription)" :icon="subscription.starts_in_future ? 'el-icon-delete' : 'el-icon-switch-button'" type="danger" circle plain></el-button>
          </el-tooltip>
        </div>
      </li>
      <li v-if="data.length" class="total subscription subscription--total">
        <span>{{ $t('Total') }}</span>
        <ul>
          <li v-for="(sum, period) in totalPriceGroupedByPeriod(data)" :key="period">
            {{ `${sum} ${data[0].cur_symbol} / ${period}` }}
          </li>
        </ul>
      </li>
    </ul>
  </card>
</template>

<script>
import { Button } from 'element-ui'
import { mapState } from 'vuex'
import axios from 'axios'
import { format, isValid, getYear, addDays, subDays } from 'date-fns'
import number from '../mixins/number'
import { chargingPeriods } from '../mixins/chargingPeriods'

export default {
  name: 'ActiveSubscriptions',
  mixins: [number, chargingPeriods],
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean
    },
    agrmid: {
      type: Number
    }
  },
  components: {
    [Button.name]: Button
  },
  data () {
    return {
      activeServices: [],
      modal: {
        item: {},
        opened: false
      },
      filterActive: 1
    }
  },
  methods: {
    goToProducts: function() {
      this.$router.push({
        name: 'createSubscription',
        params: {
          agrmid: this.agrmid
        }
      })
    },
    editProduct: function(subscription) {
      let timefrom = new Date(subscription.timefrom)
      let minTimefrom = new Date(subscription.timefrom)
      if (subscription.starts_today || subscription.starts_in_past) {
        timefrom = new Date()
        minTimefrom = subDays(new Date(), 1)
      }
      if (subscription.starts_in_future) {
        minTimefrom = subDays(new Date(), 1)
      }
      console.log('timefrom', subscription.timefrom)
      this.$router.push({
        name: 'editSubscription',
        params: {
          editing: true,
          productId: subscription.product_id,
          periodId: subscription.period_id,
          agrmid: subscription.agrm_id,
          subscriptionId: subscription.subscription_id,
          timefrom: timefrom,
          minTimefrom: minTimefrom,
          globalId: subscription.global_id,
          timeto: subscription.timeto,
          starts_in_future: subscription.starts_in_future,
          starts_today: subscription.starts_today
        }
      })
    },
    showPlannedChanges: function(subscription) {
      let minTimefrom = new Date()
      if (this.$route.params.starts_today) {
        minTimefrom = addDays(minTimefrom, 1)
      }
      this.$router.push({
        name: 'editSubscription',
        params: {
          editing: true,
          productId: subscription.product_id,
          agrmid: subscription.agrm_id,
          subscriptionId: subscription.next_subscription_id,
          globalId: subscription.global_id,
          timefrom: new Date(subscription.global_next_change_date),
          minTimefrom: minTimefrom,
          timeto: subscription.timeto,
          starts_in_future: subscription.starts_in_future,
          starts_today: subscription.starts_today,
          showPlannedChanges: true
        }
      })
    },
    confirmDelete: function(subscription) {
      this.modal.item = subscription
      this.modal.opened = true
    },
    closeSubscription: function(subscription) {
      axios.delete('subscriptions/' + subscription.subscription_id, {
        data: { starts_in_future: subscription.starts_in_future }
      }).then(promise => {
        this.modal.opened = false
        this.$emit('subscriptionDeleted')
        this.$notify(
          {
            message: this.$t('Subscription cancelled'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
      }).catch(error => {
        this.modal.opened = false
        this.$notify(
          {
            message: error.response.data.error,
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'danger'
          })
      })
    },
    processedTooltip: function(subscription) {
      if (subscription.current_blocking) {
        return this.$t(subscription.current_blocking === 3 ? 'Subscription has been blocked by operator' : 'Not enough funds for subscription')
      }
      const date = new Date(subscription.timefrom)

      return subscription.starts_in_future ? this.$t('Will start on') + ' ' + format(date, 'dd.MM.yyyy') : this.$t('Processing')
    },
    period: function(subscription) {
      const timefrom = new Date(subscription.global_start_date)
      const timeto = new Date(subscription.global_end_date)
      const timetoIsValid = isValid(timeto) && getYear(timeto) !== 9999
      const timefromString = `${this.$t('Since').toLowerCase()} ${format(timefrom, 'dd.MM.yyyy')}`
      const timetoString = timetoIsValid ? `${this.$t('Till').toLowerCase()} ${format(timeto, 'dd.MM.yyyy')}` : ''

      return `${timefromString} ${timetoString}`
    },
    hasPlannedChanges: function(subscription) {
      const changeDate = new Date(subscription.global_next_change_date)
      return isValid(changeDate) && subscription.global_next_change_date !== null
    },
    plannedChangesTooltip: function(subscription) {
      const nextChangeDate = new Date(subscription.global_next_change_date)
      return this.$t('Changes planned from {date}', { date: format(nextChangeDate, 'dd.MM.yyyy') })
    },
    editIsDisabled: function(subscription) {
      if (subscription.state <= 0 && !subscription.starts_in_future) {
        return true
      }
      if (subscription.pc_unavailable === 1) {
        return true
      }
      if (this.hasPlannedChanges(subscription)) {
        return true
      }
      if (subscription.timeto && subscription.timeto !== '9999-12-31 23:59:59' && !subscription.starts_in_future) {
        return true
      }

      return false
    },
    deleteIsDisabled: function(subscription) {
      if (subscription.ends_today) {
        return true
      }
      if (subscription.state <= 0 && !subscription.starts_in_future) {
        return true
      }
      if (subscription.pc_unavailable) {
        return true
      }

      return false
    }
  },
  computed: {
    ...mapState({
      subscriptionsMenu: state => state.config.data.menu.subscriptions,
      allowCreate: state => state.config.data.allowCreateSubscription,
      allowEdit: state => state.config.data.allowEditSubscription,
      allowStop: state => state.config.data.allowStopSubscription
    })
  }
}
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
  padding-left: 0 !important;
  padding-right: 0 !important;
}
h4 {
  margin: 0;
  margin-right: 10px;
}
.new-button {
  margin: 0;
  margin-left: auto;
}
ul {
  padding: 0;
  margin: 0;
  list-style: none;
}
.subscription {
  display: flex;
  align-items: center;
  padding: 20px 15px;
  margin-left: -15px;
  margin-right: -15px;
  &:hover {
    background-color: #f5f7fa;
    border-radius: 5px;
  }
  &--total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 18px;
    background-color: #f5f7fa;
  }
  &__meta {
    margin-right: 20px;
  }
  &__name {
    font-size: 18px;
    line-height: 1;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    &--active {
      &:before {
        content: "·";
        font-size: 60px;
        vertical-align: middle;
        line-height: 20px;
        color: #18ce0f;
      }
    }
  }
  &__price {
    margin-left: auto;
    font-size: 20px;
    line-height: 1.2;
    font-weight: 200;
    flex: 1 1 auto;
    text-align: right;
  }
  &__date {
    padding-left: 16px;
    color: #9A9A9A;
    font-weight: 300;
    line-height: 1.5;
  }
  &__period {
    font-size: 14px;
    color: #9A9A9A;
    font-weight: 300;
    line-height: 1.5;
  }
  &__customName {
    padding-left: 16px;
  }
  .actions {
    display: flex;
    flex-direction: column;
    margin-left: 15px;
    flex: 0 0 auto;
    & > .el-button {
      margin: 0;
      &:first-child {
        margin-bottom: 5px;
        @media (min-width: 576px) {
          margin-bottom: 0;
          margin-right: 10px;
        }
      }
    }
    @media (min-width: 576px) {
      flex-direction: row;
    }
  }
}
.empty {
  text-align: center;
  color: #909399
}

</style>
